import dayjs, { type Dayjs } from "dayjs";

import { nästaVardag } from "./helgdagar";
import { SVENSKA_MÅNADER } from "../moms/momsPeriod";

import type { IRäkenskapsår } from "../../types/nya/common";

/**
 * Regelmotor för myndighetsdatum.
 *
 * Skatteverket publicerar inget API för deklarationstidpunkter. Datumen behöver
 * inte hämtas: de är fullt deterministiska funktioner av räkenskapsåret,
 * momsredovisningsperioden och organisationsformen, och reglerna står i
 * skatteförfarandelagen (SFL). Motorn räknar därför ut dem lokalt.
 *
 * Alla regler ligger samlade i den här filen med lagrumshänvisning per regel,
 * så att en framtida lagändring är en ändring på ett ställe.
 */

// ----------------------------------------------------------------------

export type Myndighet = "Skatteverket" | "Bolagsverket" | "Föreningen";

export type UppgiftsTyp =
  | "ARBETSGIVARDEKLARATION"
  | "MOMSDEKLARATION"
  | "PRELIMINÄRSKATT"
  | "INKOMSTDEKLARATION"
  | "ÅRSREDOVISNING"
  | "KONTROLLUPPGIFT"
  | "STÄMMA"
  | "FASTIGHETSDEKLARATION";

export type Myndighetsuppgift = {
  /** Stabil nyckel över omräkningar — används för att bocka av en uppgift. */
  id: string;
  typ: UppgiftsTyp;
  titel: string;
  detalj?: string;
  myndighet: Myndighet;
  /** Datumet som gäller, efter framflyttning över helg och helgdag. */
  förfallodag: Dayjs;
  /** Datumet före framflyttning. Skiljer sig när regeldatumet var en helgdag. */
  regeldatum: Dayjs;
  lagrum: string;
};

export type Deklarationsprofil = {
  organisationsTyp?: string;
  räkenskapsår?: IRäkenskapsår[];
  momsDeklarationsPeriod?: "ÅRLIG" | "KVARTAL" | "MÅNAD";
  /** Genererar arbetsgivardeklarationer varje månad. */
  ärArbetsgivare: boolean;
  /** Genererar månatliga betalningspåminnelser för debiterad F-skatt. */
  harDebiteradPreliminärskatt: boolean;
  /** Digital inlämning ger en månads senare deklarationstidpunkt. */
  lämnarDigitalt: boolean;
  /**
   * Om profilen avser någon som äger ett småhus — villa, radhus, kedjehus eller parhus.
   *
   * Den enda uppgiften här som inte handlar om en organisation. Fastighetsdeklarationen lämnas av
   * den fysiska person som äger huset, och skyldigheten följer av fastigheten i stället för av ett
   * räkenskapsår. Utan flaggan genereras ingen påminnelse: en bostadsrättshavare ska inte nagas om
   * en deklaration hen aldrig ska lämna.
   */
  ägerSmåhus?: boolean;
  /**
   * Antal överlåtelser per inkomstår, för kontrolluppgiften KU55.
   *
   * Utan uppgiften genereras ingen KU-påminnelse. En förening där ingen sålt ska inte nagas om
   * en kontrolluppgift som inte ska lämnas, och att gissa på att det skett en försäljning vore
   * värre än att vara tyst.
   */
  antalÖverlåtelserPerÅr?: Record<number, number>;
  /**
   * När kontot började finnas i appen. Uppgifter som förföll dessförinnan tas
   * inte med: appen har ingenting att säga om tiden innan den fanns, och en röd
   * påminnelse om en tidpunkt som passerade före registreringen är alltid ett
   * falsklarm. Utan datum filtreras ingenting bort.
   */
  iAppenSedan?: Dayjs;
};

// ----------------------------------------------------------------------

export function toDayjs(värde: unknown): Dayjs | null {
  if (!värde) return null;
  if (typeof (värde as Dayjs)?.format === "function") return värde as Dayjs;
  const parsad = dayjs(värde as string);
  return parsad.isValid() ? parsad : null;
}

const månadsnamn = (månadsIndex: number) => SVENSKA_MÅNADER[månadsIndex];

/**
 * Deklarations- och betalningsdag i en given månad enligt huvudregeln i
 * SFL 26 kap. 26 § och 62 kap. 3 §: den 12:e, men den 17:e i januari och
 * augusti.
 */
function deklarationsdag(år: number, månadsIndex: number): Dayjs {
  const dag = månadsIndex === 0 || månadsIndex === 7 ? 17 : 12;
  return dayjs(new Date(år, månadsIndex, dag));
}

function skapa(
  uppgift: Omit<Myndighetsuppgift, "förfallodag" | "regeldatum"> & {
    regeldatum: Dayjs;
  },
): Myndighetsuppgift {
  return {
    ...uppgift,
    regeldatum: uppgift.regeldatum,
    förfallodag: nästaVardag(uppgift.regeldatum),
  };
}

// ----------------------------------------------------------------------
// Inkomstdeklaration
// ----------------------------------------------------------------------

/**
 * Deklarationstidpunkt för juridiska personer, SFL 32 kap. 2 §. Bokföringslagen
 * 3 kap. 1 § tillåter bara fyra bokslutsdatum, så tabellen är uttömmande.
 * Digital inlämning ger en månads anstånd.
 */
/** [månadsIndex, dag, antal år efter räkenskapsårets slut] */
type Tidpunkt = [månadsIndex: number, dag: number, årTillägg: number];

const INKOMSTDEKLARATION_TIDPUNKT: Record<
  string,
  { papper: Tidpunkt; digitalt: Tidpunkt }
> = {
  "12-31": { papper: [6, 1, 1], digitalt: [7, 1, 1] }, // 1 juli / 1 augusti
  "04-30": { papper: [10, 1, 0], digitalt: [11, 1, 0] }, // 1 november / 1 december
  "06-30": { papper: [11, 15, 0], digitalt: [0, 15, 1] }, // 15 december / 15 januari
  "08-31": { papper: [2, 1, 1], digitalt: [3, 1, 1] }, // 1 mars / 1 april
};

function inkomstdeklarationsdag(
  sistaDag: Dayjs,
  lämnarDigitalt: boolean,
): Dayjs | null {
  const regel = INKOMSTDEKLARATION_TIDPUNKT[sistaDag.format("MM-DD")];
  if (!regel) return null;

  const [månadsIndex, dag, årTillägg] = lämnarDigitalt
    ? regel.digitalt
    : regel.papper;
  return dayjs(new Date(sistaDag.year() + årTillägg, månadsIndex, dag));
}

// ----------------------------------------------------------------------
// Motorn
// ----------------------------------------------------------------------

/**
 * Hur långt fram de återkommande serierna genereras: arbetsgivardeklaration,
 * preliminärskatt och moms. De ger en post per period, så ett långt fönster gör
 * listan till en kalender där ungefär en rad är den intressanta.
 */
export const STANDARD_HORISONT_DAGAR = 90;

/**
 * Hur långt fram de årsbundna uppgifterna tas med — stämma, årsredovisning,
 * inkomstdeklaration, helårsmoms, fastighetsdeklaration, KU55. De är som mest
 * några stycken per räkenskapsår och staplas inte, och de ska synas i god tid.
 *
 * Värdet är också listans yttersta gräns, eftersom serierna redan begränsas av
 * sina egna loopar.
 */
export const STANDARD_ÅRSHORISONT_DAGAR = 100;

/** Hur långt bakåt förfallna uppgifter tas med. */
export const STANDARD_HISTORIK_DAGAR = 120;

export type BeräkningsAlternativ = {
  idag?: Dayjs;
  /** Se {@link STANDARD_HORISONT_DAGAR}. */
  horisontDagar?: number;
  /** Se {@link STANDARD_ÅRSHORISONT_DAGAR}. Sätt den tillsammans med `horisontDagar` för att klippa allt vid samma gräns. */
  årshorisontDagar?: number;
  /** Se {@link STANDARD_HISTORIK_DAGAR}. */
  historikDagar?: number;
};

export function beräknaMyndighetsuppgifter(
  profil: Deklarationsprofil,
  alternativ: BeräkningsAlternativ = {},
): Myndighetsuppgift[] {
  const idag = (alternativ.idag ?? dayjs()).startOf("day");
  const horisont = idag.add(
    alternativ.horisontDagar ?? STANDARD_HORISONT_DAGAR,
    "day",
  );
  const årshorisont = idag.add(
    alternativ.årshorisontDagar ?? STANDARD_ÅRSHORISONT_DAGAR,
    "day",
  );
  const historik = idag.subtract(
    alternativ.historikDagar ?? STANDARD_HISTORIK_DAGAR,
    "day",
  );
  // Registreringsdagen är ett golv under historiken, aldrig ett tak över den.
  const tidigast =
    profil.iAppenSedan && profil.iAppenSedan.isAfter(historik, "day")
      ? profil.iAppenSedan.startOf("day")
      : historik;

  // Serierna får den korta horisonten, de årsbundna den långa. Det som ligger
  // utanför en series `while`-slinga är årsbundet trots att det genereras här —
  // KU55 i `arbetsgivardeklarationer` och helårsmomsen i `momsdeklarationer` —
  // och klipps därför först av slutfiltret nedan.
  const uppgifter: Myndighetsuppgift[] = [
    ...arbetsgivardeklarationer(profil, idag, horisont),
    ...preliminärskatt(profil, idag, horisont),
    ...momsdeklarationer(profil, idag, horisont),
    ...årsbundnaUppgifter(profil, idag, årshorisont),
    ...fastighetsdeklarationer(profil, idag),
  ];

  return uppgifter
    .filter(
      (u) =>
        !u.förfallodag.isBefore(tidigast, "day") &&
        !u.förfallodag.isAfter(årshorisont, "day"),
    )
    .sort((a, b) => a.förfallodag.valueOf() - b.förfallodag.valueOf());
}

// ----------------------------------------------------------------------

/**
 * Arbetsgivardeklaration lämnas månadsvis, senast den 12:e i månaden efter
 * redovisningsperioden (17:e i januari och augusti). SFL 26 kap. 26 §.
 * Samma dag ska avdragen skatt och arbetsgivaravgifter vara betalda.
 */
function arbetsgivardeklarationer(
  profil: Deklarationsprofil,
  idag: Dayjs,
  horisont: Dayjs,
): Myndighetsuppgift[] {
  if (!profil.ärArbetsgivare) return [];

  const serie: Myndighetsuppgift[] = [];
  let period = idag.subtract(6, "month").startOf("month");

  while (period.isBefore(horisont)) {
    const förfallomånad = period.add(1, "month");
    const regeldatum = deklarationsdag(
      förfallomånad.year(),
      förfallomånad.month(),
    );

    serie.push(
      skapa({
        id: `agd-${period.format("YYYY-MM")}`,
        typ: "ARBETSGIVARDEKLARATION",
        titel: `Arbetsgivardeklaration för ${månadsnamn(period.month())} ${period.year()}`,
        detalj:
          "Lämna deklarationen och betala avdragen skatt och arbetsgivaravgifter.",
        myndighet: "Skatteverket",
        regeldatum,
        lagrum: "SFL 26 kap. 26 §",
      }),
    );
    period = period.add(1, "month");
  }

  // Slingan ovan går på periodens startmånad, men det är förfallodagen som avgör
  // om posten hör hemma i fönstret — den ligger en månad senare och kan därför
  // falla utanför. Utan den här klippningen läcker serien förbi horisonten.
  const uppgifter: Myndighetsuppgift[] = serie.filter(
    (u) => !u.förfallodag.isAfter(horisont, "day"),
  );

  // Kontrolluppgift KU55 för föreningens överlåtelser, senast 31 januari året efter försäljningen.
  // SFL 22 kap. 9 § och 24 kap. 1 §. Genereras bara för år där vi vet att någon faktiskt sålt.
  // Den är årsbunden, inte en del av serien, och klipps därför av den yttre gränsen.
  for (const [årStr, antal] of Object.entries(
    profil.antalÖverlåtelserPerÅr ?? {},
  )) {
    if (!antal || antal < 1) continue;
    const inkomstår = Number(årStr);
    uppgifter.push(
      skapa({
        id: `ku55-${inkomstår}`,
        typ: "KONTROLLUPPGIFT",
        titel: `Lämna kontrolluppgift KU55 för ${inkomstår}`,
        detalj: `${antal} ${antal === 1 ? "överlåtelse" : "överlåtelser"} under ${inkomstår}. Kontrolluppgiften ska till Skatteverket, med kopia till säljaren.`,
        myndighet: "Skatteverket",
        regeldatum: dayjs(new Date(inkomstår + 1, 0, 31)),
        lagrum: "SFL 22 kap. 9 §",
      }),
    );
  }

  return uppgifter;
}

/**
 * Debiterad preliminärskatt betalas den 12:e varje månad, den 17:e i januari
 * och augusti. SFL 62 kap. 3 §.
 */
function preliminärskatt(
  profil: Deklarationsprofil,
  idag: Dayjs,
  horisont: Dayjs,
): Myndighetsuppgift[] {
  if (!profil.harDebiteradPreliminärskatt) return [];

  const uppgifter: Myndighetsuppgift[] = [];
  let månad = idag.subtract(3, "month").startOf("month");

  while (månad.isBefore(horisont)) {
    uppgifter.push(
      skapa({
        id: `fskatt-${månad.format("YYYY-MM")}`,
        typ: "PRELIMINÄRSKATT",
        titel: `Betala debiterad preliminärskatt, ${månadsnamn(månad.month())}`,
        detalj: "Beloppet ska finnas på skattekontot senast förfallodagen.",
        myndighet: "Skatteverket",
        regeldatum: deklarationsdag(månad.year(), månad.month()),
        lagrum: "SFL 62 kap. 3 §",
      }),
    );
    månad = månad.add(1, "month");
  }

  // Samma klippning som i arbetsgivardeklarationerna: slingan går på månaden,
  // gränsen gäller förfallodagen.
  return uppgifter.filter((u) => !u.förfallodag.isAfter(horisont, "day"));
}

/**
 * Momsdeklaration. SFL 26 kap. 26–30 §§.
 *
 * MÅNAD   – senast den 12:e i andra månaden efter redovisningsperioden.
 * KVARTAL – senast den 12:e i andra månaden efter kvartalets utgång.
 * ÅRLIG   – samma tidpunkt som inkomstdeklarationen.
 *
 * Två fall är medvetet inte modellerade eftersom appen saknar underlag för att
 * avgöra dem, och båda ger ett tidigare datum än det vi visar:
 *  - beskattningsunderlag över 40 mkr ⇒ den 26:e i månaden efter perioden
 *  - årsmoms med EU-handel ⇒ den 26:e i andra månaden efter beskattningsåret
 */
function momsdeklarationer(
  profil: Deklarationsprofil,
  idag: Dayjs,
  horisont: Dayjs,
): Myndighetsuppgift[] {
  const period = profil.momsDeklarationsPeriod;
  if (!period) return [];

  if (period === "ÅRLIG") {
    return (profil.räkenskapsår ?? []).flatMap((år) => {
      const sistaDag = toDayjs(år.sistaDag);
      if (!sistaDag) return [];
      const regeldatum = inkomstdeklarationsdag(
        sistaDag,
        profil.lämnarDigitalt,
      );
      if (!regeldatum) return [];
      return [
        skapa({
          id: `moms-ar-${sistaDag.format("YYYY-MM-DD")}`,
          typ: "MOMSDEKLARATION",
          titel: `Momsdeklaration för ${sistaDag.year()}`,
          detalj: "Helårsmoms lämnas samtidigt som inkomstdeklarationen.",
          myndighet: "Skatteverket",
          regeldatum,
          lagrum: "SFL 26 kap. 33 §",
        }),
      ];
    });
  }

  const stegMånader = period === "KVARTAL" ? 3 : 1;
  const uppgifter: Myndighetsuppgift[] = [];

  let periodStart = idag.subtract(9, "month").startOf("month");
  if (period === "KVARTAL") {
    periodStart = periodStart
      .month(Math.floor(periodStart.month() / 3) * 3)
      .startOf("month");
  }

  while (periodStart.isBefore(horisont)) {
    const periodSlut = periodStart.add(stegMånader - 1, "month").endOf("month");
    const förfallomånad = periodSlut.add(2, "month");
    const regeldatum = deklarationsdag(
      förfallomånad.year(),
      förfallomånad.month(),
    );

    const etikett =
      stegMånader === 1
        ? `${månadsnamn(periodStart.month())} ${periodStart.year()}`
        : `${månadsnamn(periodStart.month())}–${månadsnamn(periodSlut.month())} ${periodSlut.year()}`;

    uppgifter.push(
      skapa({
        id: `moms-${periodStart.format("YYYY-MM")}`,
        typ: "MOMSDEKLARATION",
        titel: `Momsdeklaration för ${etikett}`,
        detalj: "Lämna deklarationen och betala momsen samma dag.",
        myndighet: "Skatteverket",
        regeldatum,
        lagrum: "SFL 26 kap. 26 §",
      }),
    );
    periodStart = periodStart.add(stegMånader, "month");
  }

  // Momsen förfaller två månader efter periodens slut, så en period som ryms
  // inom horisonten kan ha en förfallodag långt utanför den — för kvartalsmoms
  // ett halvår senare. Gränsen gäller förfallodagen. Helårsmomsen är årsbunden
  // och har redan returnerat ovan.
  return uppgifter.filter((u) => !u.förfallodag.isAfter(horisont, "day"));
}

// ----------------------------------------------------------------------
// Fastighetstaxering, småhus
// ----------------------------------------------------------------------

/**
 * Småhus taxeras vart tredje år, växelvis allmänt och förenklat: 2021 allmän,
 * 2024 förenklad, 2027 allmän, 2030 förenklad. Cykeln är ankrad i 2021 därför
 * att det är den senaste allmänna taxering som går att kontrollräkna mot.
 *
 * Speglar `Fastighetstaxeringscykel.java`. Reglerna finns i dag på båda sidor
 * — backendmotorn är inte exponerad över något API — så en ändring här måste
 * göras där också.
 */
const TAXERING_ANKARÅR = 2021;
const TAXERING_INTERVALL_ÅR = 3;

function ärTaxeringsår(år: number): boolean {
  return (
    (((år - TAXERING_ANKARÅR) % TAXERING_INTERVALL_ÅR) +
      TAXERING_INTERVALL_ÅR) %
      TAXERING_INTERVALL_ÅR ===
    0
  );
}

function taxeringsslag(taxeringsår: number): string {
  const antalCykler = Math.floor(
    (taxeringsår - TAXERING_ANKARÅR) / TAXERING_INTERVALL_ÅR,
  );
  return ((antalCykler % 2) + 2) % 2 === 0
    ? "allmän fastighetstaxering"
    : "förenklad fastighetstaxering";
}

/**
 * Fastighetsdeklaration för småhusenhet, senast den 1 november året före
 * taxeringsåret. FTL 18 kap. 9 §.
 *
 * `nästaVardag` ger datumen Skatteverket faktiskt annonserar: 1 november 2026
 * är en söndag, alltså 2 november inför taxeringen 2027.
 *
 * Genereras bara för den som äger ett småhus, av samma skäl som
 * kontrolluppgiften bara genereras för år då vi vet att någon sålt. Fönstret i
 * `beräknaMyndighetsuppgifter` trimmar bort de år som ligger för långt bort.
 */
function fastighetsdeklarationer(
  profil: Deklarationsprofil,
  idag: Dayjs,
): Myndighetsuppgift[] {
  if (!profil.ägerSmåhus) return [];

  const uppgifter: Myndighetsuppgift[] = [];

  for (
    let taxeringsår = idag.year();
    taxeringsår <= idag.year() + 2;
    taxeringsår += 1
  ) {
    if (!ärTaxeringsår(taxeringsår)) continue;

    const skickDag = dayjs(new Date(taxeringsår, 0, 1));

    uppgifter.push(
      skapa({
        id: `fastighetsdeklaration-smahus-${taxeringsår}`,
        typ: "FASTIGHETSDEKLARATION",
        titel: `Deklarera småhus inför ${taxeringsslag(taxeringsår)} ${taxeringsår}`,
        detalj: `Uppgifterna ska avse fastighetens skick den ${skickDag.format("YYYY-MM-DD")}. Lämnas i Skatteverkets e-tjänst eller på blankett.`,
        myndighet: "Skatteverket",
        // Månadsindex 10 = november.
        regeldatum: dayjs(new Date(taxeringsår - 1, 10, 1)),
        lagrum: "FTL 18 kap. 9 §",
      }),
    );
  }

  return uppgifter;
}

/**
 * Uppgifter som följer av räkenskapsåret: inkomstdeklaration, stämma och
 * — för aktiebolag — årsredovisning till Bolagsverket.
 */
function årsbundnaUppgifter(
  profil: Deklarationsprofil,
  idag: Dayjs,
  horisont: Dayjs,
): Myndighetsuppgift[] {
  const uppgifter: Myndighetsuppgift[] = [];
  const ärAktiebolag = profil.organisationsTyp === "AKTIEBOLAG";
  // En enskild firma är ingen egen juridisk person. Den håller varken bolags- eller
  // föreningsstämma, och lämnar ingen årsredovisning till Bolagsverket — innehavaren redovisar
  // verksamheten på NE-bilagan till sin egen inkomstdeklaration.
  const ärEnskildFirma = profil.organisationsTyp === "ENSKILD_FIRMA";
  const ärPrivatperson = profil.organisationsTyp === "PRIVATPERSON";

  if (ärPrivatperson) {
    // Inkomstdeklaration 1 lämnas senast den 2 maj. SFL 32 kap. 1 §.
    //
    // Bara tidpunkter som ännu inte passerat. En privatperson har ingen
    // bokföring i appen, så en passerad deklarationstidpunkt kan aldrig bockas
    // av härledd väg — den skulle ligga kvar och larma om något som med all
    // sannolikhet redan är gjort, och som appen ändå inte kan uttala sig om.
    return [idag.year(), idag.year() + 1]
      .map((år) =>
        skapa({
          id: `ind1-${år}`,
          typ: "INKOMSTDEKLARATION",
          titel: `Inkomstdeklaration 1 för ${år - 1}`,
          detalj: "Deklarationen ska ha kommit in till Skatteverket.",
          myndighet: "Skatteverket",
          regeldatum: dayjs(new Date(år, 4, 2)),
          lagrum: "SFL 32 kap. 1 §",
        }),
      )
      .filter((uppgift) => !uppgift.förfallodag.isBefore(idag, "day"));
  }

  for (const år of profil.räkenskapsår ?? []) {
    const sistaDag = toDayjs(år.sistaDag);
    if (!sistaDag) continue;
    const nyckel = sistaDag.format("YYYY-MM-DD");

    const deklarationsdatum = inkomstdeklarationsdag(
      sistaDag,
      profil.lämnarDigitalt,
    );
    if (deklarationsdatum && !deklarationsdatum.isAfter(horisont, "day")) {
      uppgifter.push(
        skapa({
          id: `ink-${nyckel}`,
          typ: "INKOMSTDEKLARATION",
          titel: `Inkomstdeklaration för räkenskapsåret som slutar ${sistaDag.format("YYYY-MM-DD")}`,
          detalj: profil.lämnarDigitalt
            ? "Digital inlämning. På papper gäller en månad tidigare."
            : "Pappersinlämning. Digitalt ger en månad extra.",
          myndighet: "Skatteverket",
          regeldatum: deklarationsdatum,
          lagrum: "SFL 32 kap. 2 §",
        }),
      );
    }

    // Stämma inom sex månader från räkenskapsårets utgång.
    // ABL 7 kap. 10 § för aktiebolag, EFL 6 kap. 11 § för ekonomiska föreningar.
    // Den enskilda firman har ingen stämma alls — den har varken aktieägare eller medlemmar.
    if (!ärEnskildFirma) {
      uppgifter.push(
        skapa({
          id: `stamma-${nyckel}`,
          typ: "STÄMMA",
          titel: ärAktiebolag ? "Håll årsstämma" : "Håll föreningsstämma",
          detalj: `Senast sex månader efter räkenskapsårets utgång (${sistaDag.format("YYYY-MM-DD")}).`,
          myndighet: "Föreningen",
          regeldatum: sistaDag.add(6, "month"),
          lagrum: ärAktiebolag ? "ABL 7 kap. 10 §" : "EFL 6 kap. 11 §",
        }),
      );
    }

    // Aktiebolag ska ge in årsredovisningen inom sju månader. ÅRL 8 kap. 3 §.
    //
    // Sedan lagändringen som trädde i kraft 2025-01-01 gäller registreringsplikten även
    // ekonomiska föreningar och bostadsrättsföreningar, första gången för det räkenskapsår som
    // inleds närmast efter 2024-12-31. Kommentaren här sa tidigare att de inte omfattas, vilket
    // slutade stämma i och med den ändringen.
    const förstaDag = toDayjs(år.förstaDag);
    const omfattasAvRegistreringsplikt =
      !ärEnskildFirma &&
      (ärAktiebolag || (förstaDag != null && förstaDag.year() >= 2025));

    if (omfattasAvRegistreringsplikt) {
      uppgifter.push(
        skapa({
          id: `arsredovisning-${nyckel}`,
          typ: "ÅRSREDOVISNING",
          titel: "Lämna årsredovisning till Bolagsverket",
          detalj: ärAktiebolag
            ? "Senast sju månader efter räkenskapsårets utgång. Därefter förseningsavgift."
            : "Senast sju månader efter räkenskapsårets utgång. Gäller föreningar sedan räkenskapsår 2025, och förseningsavgiften är 7 500 kr.",
          myndighet: "Bolagsverket",
          regeldatum: sistaDag.add(7, "month"),
          lagrum: "ÅRL 8 kap. 3 §",
        }),
      );
    }
  }

  return uppgifter;
}
